'use client';
import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  autoScrollToTop?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
  className = "",
  autoScrollToTop = true,
}) => {
  if (totalPages <= 1) return null;

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;

    onPageChange(page);

    // Auto-scroll on page change (unless disabled)
    if (autoScrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Keyboard nav (← prev, → next)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPage(currentPage - 1);
      if (e.key === "ArrowRight") goToPage(currentPage + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [currentPage, totalPages]);

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    const range = 1;

    if (currentPage > 2) {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
    }

    for (
      let i = Math.max(1, currentPage - range);
      i <= Math.min(totalPages, currentPage + range);
      i++
    ) {
      pages.push(i);
    }

    if (currentPage < totalPages - 1) {
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <nav
      aria-label="Pagination"
      className={`flex justify-center mt-8 ${className}`}
    >
      {/* Mobile View */}
      <div className="flex items-center space-x-2 sm:hidden">
        <button
          aria-label="Previous page"
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} />
        </button>

        <span className="px-3 py-1 font-medium">
          {currentPage} / {totalPages}
        </span>

        <button
          aria-label="Next page"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(currentPage + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Desktop View */}
      <div className="hidden sm:flex items-center space-x-2">
        <button
          aria-label="Previous page"
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
          className="px-3 py-1 border rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={24} />
        </button>

        {renderPageNumbers().map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-2 py-1 select-none">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => goToPage(p as number)}
              aria-current={currentPage === p ? "page" : undefined}
              className={`px-3 py-1 border rounded cursor-pointer ${
                currentPage === p ? "bg-blue-500 text-white" : ""
              }`}
              aria-label={`Go to page ${p}`}
            >
              {p}
            </button>
          )
        )}

        <button
          aria-label="Next page"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(currentPage + 1)}
          className="px-3 py-1 border rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
